import type { components } from '@app/api/types';
import { dayMonth, hour } from '@app/format';
import { t } from '@app/i18n';

/**
 * A FOTO DO RUNTIME NA TELA (aim-6ph.13, protótipo aprovado em 10/10/2026).
 *
 * O estado do cluster e do PDP sai de uma foto guardada no Postgres (regra 11). Cada resposta que depende dela traz
 * um bloco `reading`: de quando é a foto (`read_at`), a última tentativa (`tried_at`), se uma leitura nova está em
 * curso (`in_flight`) e o código da última falha (`error_code`). A tela abre com a foto, dispara a leitura nova
 * (`useClusterReading`) e relê enquanto `in_flight` (`readingInterval`).
 *
 * Os quatro estados de cada bloco, com as frases do protótipo:
 *
 *   A. foto, sem leitura em curso        «lido às 14:05»
 *   B. foto, lendo de novo               «lido às 14:05 · lendo o cluster de novo…»
 *   C. foto, a leitura nova falhou       «lido às 14:05 · não foi possível ler de novo (middleware_fora)»
 *   D. nunca lido                        «ainda não lido · lendo o cluster…», ou a falha da primeira leitura
 *
 * O `data-state` do elemento leva a letra: os testes afirmam a ESTRUTURA, nunca a frase.
 */

export type ReadingState = components['schemas']['ReadingState'];
export type ReadingScope = components['schemas']['RuntimeReadingRequest']['scope'];

/** De onde vem a foto: o cluster (L7, L10 e o `/debug/status`) ou o PDP (L8 e L9). Muda só a frase. */
export type ReadingFamily = 'cluster' | 'pdp';

export type ReadingLetter = 'A' | 'B' | 'C' | 'D';

export interface ReadingView {
  state: ReadingLetter;
  /** O `{reading}` que as frases da tela compõem. Vazio quando não há o que dizer. */
  text: string;
  /**
   * A leitura passou do teto sem gravar nada: `tried_at` depois de `read_at`, sem leitura em curso e sem código. A
   * tela diz o que sabe, «não foi possível confirmar a leitura nova», e não que a leitura falhou.
   */
  unconfirmed: boolean;
}

export interface ReadingViewOptions {
  family?: ReadingFamily;
  /**
   * A tela acabou de pedir a leitura e a resposta do `POST` ainda não voltou. A foto ainda diz «nada em curso», e a
   * tela já sabe que está lendo: o estado é B (ou D lendo), e não A por um instante.
   */
  pending?: boolean;
  /**
   * `read` dá «lido às 14:05»; `at` dá «às 14:05», para a frase de fora que já tem o verbo («o que o PDP concedia às
   * 14:05», a conferência com o PDP de T5).
   */
  form?: 'read' | 'at';
  now?: Date;
}

/**
 * De quanto em quanto tempo a tela relê enquanto a leitura nova corre. Dois segundos: a leitura do cluster leva de 1 a
 * 11 s no laboratório, e o teto de 2 min do servidor (`in_flight_until`) para a releitura sozinho, porque depois dele
 * o bloco deixa de dizer `in_flight`.
 */
export const READING_POLL_MS = 2_000;

/**
 * O `refetchInterval` de uma consulta que mostra a foto: relê enquanto qualquer um dos blocos da resposta diz que a
 * leitura nova corre, e para quando todos terminam. É o próprio dado que liga e desliga, como no Discovery.
 */
export function readingInterval(...readings: Array<ReadingState | null | undefined>): number | false {
  return readings.some((reading) => reading?.in_flight) ? READING_POLL_MS : false;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** «lido às 14:05» no mesmo dia, «lido em 09/10 14:05» em outro (decisão 7). */
function when(readAt: string, form: 'read' | 'at', now: Date): string {
  const time = hour(readAt) ?? '';
  const date = new Date(readAt);
  if (sameDay(date, now)) {
    return t(form === 'read' ? 'common.reading.readAt' : 'common.reading.at', { time });
  }
  return t(form === 'read' ? 'common.reading.readOn' : 'common.reading.on', {
    date: dayMonth(readAt) ?? '',
    time,
  });
}

function isUnconfirmed(reading: ReadingState): boolean {
  if (reading.in_flight || reading.error_code || !reading.tried_at) return false;
  if (!reading.read_at) return true;
  return new Date(reading.tried_at).getTime() > new Date(reading.read_at).getTime();
}

// O « · » é pontuação, e por isso se compõe aqui e não no catálogo (protótipo, §0): uma chave com ele seria igual nos
// dois idiomas e nunca poderia divergir.
const SEPARATOR = ' · ';

/**
 * O estado e a frase de um bloco. `null` quando a resposta não traz o bloco: aí a tela não diz nada sobre a foto, e
 * isso não é o mesmo que «nunca lido» (D), que é o bloco com `read_at` nulo.
 */
export function readingView(
  reading: ReadingState | null | undefined,
  options: ReadingViewOptions = {},
): ReadingView | null {
  if (!reading) return null;
  const family = options.family ?? 'cluster';
  const inFlight = reading.in_flight || Boolean(options.pending);
  const unconfirmed = !options.pending && isUnconfirmed(reading);
  const code = reading.error_code ?? '';

  if (!reading.read_at) {
    let text = '';
    if (inFlight) text = t(`common.reading.${family}.first`);
    else if (reading.error_code) text = t(`common.reading.${family}.failedFirst`, { code });
    else if (unconfirmed) text = t('common.reading.unconfirmed');
    return { state: 'D', text, unconfirmed };
  }

  const at = when(reading.read_at, options.form ?? 'read', options.now ?? new Date());
  if (inFlight) {
    return { state: 'B', text: at + SEPARATOR + t(`common.reading.${family}.again`), unconfirmed };
  }
  if (reading.error_code) {
    // `failedAgain` serve às duas famílias: a frase não nomeia a fonte, porque a fonte já está no bloco (protótipo, §0).
    return { state: 'C', text: at + SEPARATOR + t('common.reading.cluster.failedAgain', { code }), unconfirmed };
  }
  if (unconfirmed) {
    return { state: 'A', text: at + SEPARATOR + t('common.reading.unconfirmed'), unconfirmed };
  }
  return { state: 'A', text: at, unconfirmed };
}
